import {
  Box,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";

import { EditFieldButton } from "@/shared/components/EditFieldButton";
import {
  ComputeRequirements,
  parseComputeRequirements,
} from "@/shared/domain/actions";

import { ActionComputeReqs } from "./ActionComputeReqs";
import { ActionComputeReqsTable } from "./ActionComputeReqsTable";
import { ClearFieldButton } from "./ClearFieldButton";

interface EditableComputeReqsProps<UpdateType> {
  title: string;
  field: keyof UpdateType;
  value: ComputeRequirements;
  onChange: (updates: UpdateType) => void;
  setErrorText: (arg: string) => void;
  overrides?: boolean;
  canEdit?: boolean;
}

export default function EditableComputeReqs<UpdateType>({
  title,
  field,
  value,
  onChange,
  setErrorText,
  overrides,
  canEdit,
}: EditableComputeReqsProps<UpdateType>) {
  const theme = useTheme();
  const [showEditableIcon, setShowEditableIcon] = useState(false);
  const [editable, setEditable] = useState(false);
  const [cpu, setCpu] = useState<string | undefined>(
    value.vCPU ? value.vCPU.toString() : undefined,
  );
  const [memory, setMemory] = useState<string | undefined>(
    value.memory ? value.memory.toString() : undefined,
  );
  const [gpu, setGpu] = useState<string | undefined>(
    value.gpu ? value.gpu.toString() : undefined,
  );
  const [storage, setStorage] = useState<number | undefined>(value.storage);

  const [hasComputeFormErrors, setHasComputeFormErrors] = useState(false);

  function updateRecord(reset?: boolean) {
    const computeRequirements = parseComputeRequirements(
      cpu,
      memory,
      gpu,
      storage,
    );

    const updates = {
      [field]: reset ? null : computeRequirements,
    };
    setErrorText("");
    onChange(updates as UpdateType);
  }

  return (
    <ListItem
      onMouseOver={() => {
        setShowEditableIcon(true);
      }}
      onMouseLeave={() => {
        setShowEditableIcon(false);
      }}
    >
      <ListItemText
        primaryTypographyProps={{
          component: "span",
        }}
        primary={
          <>
            <span>{title}</span>

            <EditFieldButton
              disabled={(!editable && !canEdit) || hasComputeFormErrors}
              editable={editable}
              showEditableIcon={showEditableIcon}
              onClick={() => {
                if (editable) {
                  void updateRecord();
                }
                setEditable(!editable);
              }}
            />

            {overrides && (
              <ClearFieldButton
                disabled={!editable && !canEdit}
                showIcon={showEditableIcon}
                onClick={() => {
                  void updateRecord(true);
                  setCpu(undefined);
                  setGpu(undefined);
                  setMemory(undefined);
                  setStorage(undefined);
                }}
              />
            )}

            <Box>
              {!editable ? (
                <>
                  {value && Object.keys(value).length > 0 ? (
                    <Box
                      sx={{
                        mt: theme.spacing(1),
                      }}
                    >
                      <ActionComputeReqsTable
                        cpu={cpu}
                        memory={memory}
                        gpu={gpu}
                        storage={storage}
                      />
                    </Box>
                  ) : (
                    <Typography
                      component="span"
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      None
                    </Typography>
                  )}
                </>
              ) : (
                <Box sx={{ mt: theme.spacing(0.5) }}>
                  <ActionComputeReqs
                    cpu={cpu}
                    setCpu={setCpu}
                    memory={memory}
                    setMemory={setMemory}
                    gpu={gpu}
                    setGpu={setGpu}
                    storage={storage}
                    setStorage={setStorage}
                    setHasComputeFormErrors={setHasComputeFormErrors}
                  />
                </Box>
              )}
            </Box>
          </>
        }
      />
    </ListItem>
  );
}
